import { DragDropModule } from "@angular/cdk/drag-drop";
import { OverlayModule } from "@angular/cdk/overlay";
import { CdkTableModule } from "@angular/cdk/table";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { ErrorHandler, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatBadgeModule } from "@angular/material/badge";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatChipsModule } from "@angular/material/chips";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
} from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialogModule } from "@angular/material/dialog";
import { MatDividerModule } from "@angular/material/divider";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatIconModule } from "@angular/material/icon";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import { MatPaginatorIntl, MatPaginatorModule } from "@angular/material/paginator";
import { MatSelectModule } from "@angular/material/select";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatSliderModule } from "@angular/material/slider";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatSortModule } from "@angular/material/sort";
import { MatStepperModule } from "@angular/material/stepper";
import { MatTableModule } from "@angular/material/table";
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from "@angular/material-moment-adapter";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalRedirectComponent,
  MsalService,
} from "@azure/msal-angular";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { NgSelectModule } from "@ng-select/ng-select";
import { TranslateModule } from "@ngx-translate/core";
import { BackButtonComponent } from "src/app/back-button/back-button.component";
import { ApprovedServicePriceModalComponent } from "src/app/components/admin/approved-service-price/approved-service-price-modal/approved-service-price-modal.component";
import { LocalizationLangCreateModalComponent } from "src/app/components/admin/localization/localization-lang-create-modal/localization-lang-create-modal.component";
import { LocalizationTranslationCreateModalComponent } from "src/app/components/admin/localization/localization-translation-create-modal/localization-translation-create-modal.component";
import { DragNDropComponent } from "src/app/components/drag-n-drop/drag-n-drop.component";
import { FormFieldErrorComponent } from "src/app/components/form-field-error/form-field-error.component";
import { LoadingIconComponent } from "src/app/components/loading-icon/loading-icon.component";
import { ServiceRequestServiceFormComponent } from "src/app/components/service-request/service-request-service-form/service-request-service-form.component";
import { ServiceRequestStepOneComponent } from "src/app/components/service-request/service-request-step-one/service-request-step-one.component";
import { ServiceRequestStepTwoComponent } from "src/app/components/service-request/service-request-step-two/service-request-step-two.component";
import { ServiceRequestStepperControlComponent } from "src/app/components/service-request/service-request-stepper-control/service-request-stepper-control.component";

import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { APP_DATE_FORMATS } from "./common/formatters/date-formats";
import { AppErrorHandler } from "./common/validators/app-error-handler";
import { AdminComponent } from "./components/admin/admin/admin.component";
import { AdminRemindersComponent } from "./components/admin/admin-reminders/admin-reminders.component";
import { ApprovedServicePriceComponent } from "./components/admin/approved-service-price/approved-service-price.component";
import { CompanyRemindersComponent } from "./components/admin/company-reminders/company-reminders.component";
import { ContactsComponent } from "./components/admin/contacts/contacts.component";
import { ContactsCreateModalComponent } from "./components/admin/contacts-create-modal/contacts-create-modal.component";
import { CountryRemindersComponent } from "./components/admin/country-reminders/country-reminders.component";
import { CustomerSettingsEditModalComponent } from "./components/admin/customer-settings-edit-modal/customer-settings-edit-modal.component";
import { CustomersComponent } from "./components/admin/customers/customers.component";
import { DocumentFiltersComponent } from "./components/admin/document-filters/document-filters.component";
import { LocalizationComponent } from "./components/admin/localization/localization.component";
import { LocalizationEditComponent } from "./components/admin/localization-edit/localization-edit.component";
import { PendingUsersComponent } from "./components/admin/pending-users/pending-users.component";
import { RequestedServiceListComponent } from "./components/admin/requested-service-list/requested-service-list.component";
import { UserComponent } from "./components/admin/user/user.component";
import { UsersComponent } from "./components/admin/users/users.component";
import { VisibleEventComponent } from "./components/admin/visible-event/visible-event.component";
import { AnnouncementComponent } from "./components/announcement/announcement.component";
import { CalibrationModalComponent } from "./components/calibration-modal/calibration-modal.component";
import { ChangelogModalComponent } from "./components/changelog-modal/changelog-modal.component";
import { ConfirmationDialogModalComponent } from "./components/common/confirmation-dialog/confirmation-dialog-modal.component";
import { ErrorNotificationModalComponent } from "./components/common/error-notification-modal/error-notification-modal.component";
import { CompanyListComponent } from "./components/company-list/company-list.component";
import { CompanySelectComponent } from "./components/company-select/company-select.component";
import { ContractDetailsComponent } from "./components/contract-details/contract-details.component";
import { ContractDocumentsComponent } from "./components/contract-documents/contract-documents.component";
import { ContractInfoComponent } from "./components/contract-info/contract-info.component";
import { ContractItemsComponent } from "./components/contract-items/contract-items.component";
import { ContractToolsComponent } from "./components/contract-tools/contract-tools.component";
import { ContractsComponent } from "./components/contracts/contracts.component";
import { DatepickerInputComponent } from "./components/datepicker-input/datepicker-input.component";
import { DepartmentModalComponent } from "./components/department-modal/department-modal.component";
import { DepartmentSelectComponent } from "./components/department-select/department-select.component";
import { DeptSelectComponent } from "./components/dept-select/dept-select.component";
import { DownloadFileComponent } from "./components/download-file/download-file.component";
import { EmailSelectComponent } from "./components/email-select/email-select.component";
import { FieldServiceComponent } from "./components/field-service/field-service.component";
import { FieldServiceDetailsComponent } from "./components/field-service-details/field-service-details.component";
import { FieldServiceHistoryComponent } from "./components/field-service-history/field-service-history.component";
import { FieldServiceInfoComponent } from "./components/field-service-info/field-service-info.component";
import { FieldServiceToolsComponent } from "./components/field-service-tools/field-service-tools.component";
import { FilterBarComponent } from "./components/filter-bar/filter-bar.component";
import { FilterCategoryComponent } from "./components/filter-category/filter-category.component";
import { FilterStateChipsComponent } from "./components/filter-state-chips/filter-state-chips.component";
import { FooterComponent } from "./components/footer/footer.component";
import { HeaderComponent } from "./components/header/header.component";
import { HomeComponent } from "./components/home/home.component";
import { LanguageSelectComponent } from "./components/language-select/language-select.component";
import { LocationCreateModalComponent } from "./components/location-create-modal/location-create-modal.component";
import { LocationEditModalComponent } from "./components/location-edit-modal/location-edit-modal.component";
import { LocationModalComponent } from "./components/location-modal/location-modal.component";
import { LocationMoveModalComponent } from "./components/location-move-modal/location-move-modal.component";
import { LoginComponent } from "./components/login/login.component";
import { LoginFailedComponent } from "./components/login-failed/login-failed.component";
import { MobileMenuComponent } from "./components/mobile-menu/mobile-menu.component";
import { NoAccessComponent } from "./components/no-access/no-access.component";
import { NotFoundComponent } from "./components/not-found/not-found.component";
import { NotifierComponent } from "./components/notifier/notifier.component";
import { ProgressComponent } from "./components/progress/progress.component";
import { ProgressContainerComponent } from "./components/progress-container/progress-container.component";
import { PurchaseOrderComponent } from "./components/purchase-order/purchase-order.component";
import { PurchaseOrderButtonComponent } from "./components/purchase-order-button/purchase-order-button.component";
import { RegionSelectComponent } from "./components/region-select/region-select.component";
import { ReminderModalComponent } from "./components/reminder-modal/reminder-modal.component";
import { RemindersComponent } from "./components/reminders/reminders.component";
import { RequestAccountComponent } from "./components/request-account/request-account.component";
import { SalesforceMigrationNoticeComponent } from "./components/salesforce-migration-notice/salesforce-migration-notice.component";
import { SearchModalComponent } from "./components/search-modal/search-modal.component";
import { ServiceModalComponent } from "./components/service-modal/service-modal.component";
import { FinalStepMainContentComponent } from "./components/service-request/final-step-main-content/final-step-main-content.component";
import { ServiceRequestComponent } from "./components/service-request/service-request.component";
import { ShippingPackageGraphicComponent } from "./components/service-request/shipping-package-graphic/shipping-package-graphic.component";
import { ShippingPackagesRequestFormComponent } from "./components/service-request/shipping-packages-request-form/shipping-packages-request-form.component";
import { ServiceRequestButtonComponent } from "./components/service-request-button/service-request-button.component";
import { SnackBarComponent } from "./components/snack-bar/snack-bar.component";
import { StatusTimelineComponent } from "./components/status-timeline/status-timeline.component";
import { ToolDetailsComponent } from "./components/tool-details/tool-details.component";
import { ToolExportButtonComponent } from "./components/tool-export-button/tool-export-button.component";
import { ToolHistoryComponent } from "./components/tool-history/tool-history.component";
import { ToolInfoComponent } from "./components/tool-info/tool-info.component";
import { ToolLocationComponent } from "./components/tool-location/tool-location.component";
import { ToolNotesModalComponent } from "./components/tool-notes-modal/tool-notes-modal.component";
import { ToolStatusComponent } from "./components/tool-status/tool-status.component";
import { ToolsComponent } from "./components/tools/tools.component";
import { UserSettingsComponent } from "./components/user-settings/user-settings.component";
import { UserSubscriptionComponent } from "./components/user-subscription/user-subscription.component";
import { WimtSelectComponent } from "./components/wimt-select/wimt-select.component";
import { SelectAllOnFocusDirective } from "./directives/select-all-on-focus.directive";
import { AuthInterceptor } from "./interceptors/auth-interceptor";
import { CredentialsInterceptor } from "./interceptors/credentials-interceptor";
import { FilterByPipe } from "./pipes/filter-by.pipe";
import { FirstFromSplitPipe } from "./pipes/first-from-split.pipe";
import { ReminderPipe } from "./pipes/reminder.pipe";
import { SortByPipe } from "./pipes/sort-by.pipe";
import { SplitPipe } from "./pipes/split.pipe";
import { AdminAuthGuard } from "./services/admin-auth-guard.service";
import { AdminOrCountryAdminAuthGuard } from "./services/admin-or-country-admin-auth-guard.service";
import { AuthGuard } from "./services/auth-guard.service";
import { CountryAdminAuthGuard } from "./services/countryadmin-auth-guard.service";
import { MatIconService } from "./services/mat-icon.service";
import { SubscriptionAuthGuard } from "./services/subscription-auth-guard.service";
import { WimtMatPaginatorIntl } from "./services/wimt-mat-paginator-intl.service";
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory,
} from "./utils/auth";

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    HomeComponent,
    ToolsComponent,
    ToolDetailsComponent,
    NotFoundComponent,
    LoginComponent,
    FooterComponent,
    ToolStatusComponent,
    ToolHistoryComponent,
    ToolInfoComponent,
    MobileMenuComponent,
    ServiceRequestComponent,
    PurchaseOrderComponent,
    NoAccessComponent,
    RequestAccountComponent,
    ProgressComponent,
    UsersComponent,
    UserComponent,
    LocalizationComponent,
    LocalizationEditComponent,
    AdminComponent,
    FilterByPipe,
    SortByPipe,
    FirstFromSplitPipe,
    CompanySelectComponent,
    DepartmentSelectComponent,
    WimtSelectComponent,
    UserSettingsComponent,
    RegionSelectComponent,
    CompanyListComponent,
    ProgressContainerComponent,
    ServiceRequestButtonComponent,
    PurchaseOrderButtonComponent,
    DownloadFileComponent,
    DatepickerInputComponent,
    UserSubscriptionComponent,
    StatusTimelineComponent,
    ToolLocationComponent,
    LocationModalComponent,
    DepartmentModalComponent,
    CalibrationModalComponent,
    ServiceModalComponent,
    ToolNotesModalComponent,
    RemindersComponent,
    LocationCreateModalComponent,
    ReminderPipe,
    ReminderModalComponent,
    SplitPipe,
    LocationEditModalComponent,
    EmailSelectComponent,
    LocationMoveModalComponent,
    DeptSelectComponent,
    FilterBarComponent,
    FilterCategoryComponent,
    LanguageSelectComponent,
    ContractsComponent,
    ContractDetailsComponent,
    ContractInfoComponent,
    ContractToolsComponent,
    ContractDocumentsComponent,
    ContractItemsComponent,
    FieldServiceComponent,
    FieldServiceDetailsComponent,
    FieldServiceInfoComponent,
    FieldServiceHistoryComponent,
    FieldServiceToolsComponent,
    VisibleEventComponent,
    DocumentFiltersComponent,
    RequestedServiceListComponent,
    ApprovedServicePriceComponent,
    PendingUsersComponent,
    AdminRemindersComponent,
    CompanyRemindersComponent,
    CountryRemindersComponent,
    SearchModalComponent,
    SnackBarComponent,
    NotifierComponent,
    ConfirmationDialogModalComponent,
    FilterStateChipsComponent,
    ErrorNotificationModalComponent,
    FinalStepMainContentComponent,
    ChangelogModalComponent,
    ToolExportButtonComponent,
    LoginFailedComponent,
    ContactsComponent,
    ContactsCreateModalComponent,
    CustomersComponent,
    CustomerSettingsEditModalComponent,
    AnnouncementComponent,
    SalesforceMigrationNoticeComponent,
    ShippingPackagesRequestFormComponent,
    SelectAllOnFocusDirective,
    ShippingPackageGraphicComponent,
    ServiceRequestServiceFormComponent,
    ServiceRequestStepOneComponent,
    ServiceRequestStepperControlComponent,
    ServiceRequestStepTwoComponent,
    ApprovedServicePriceModalComponent,
    LocalizationLangCreateModalComponent,
    LocalizationTranslationCreateModalComponent,
    LoadingIconComponent,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule.forRoot(),
    AppRoutingModule,
    FontAwesomeModule,
    CdkTableModule,
    BrowserAnimationsModule,
    MatDialogModule,
    NgSelectModule,
    OverlayModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    DragDropModule,
    MatBadgeModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSidenavModule,
    MatSelectModule,
    MatButtonModule,
    MatChipsModule,
    MatPaginatorModule,
    MatExpansionModule,
    MatCheckboxModule,
    MatSliderModule,
    MatCardModule,
    MatDividerModule,
    MatAutocompleteModule,
    MatStepperModule,
    MatTableModule,
    MatSortModule,
    MatListModule,
    MatMenuModule,
    DragNDropComponent,
    BackButtonComponent,
    FormFieldErrorComponent,
  ],
  providers: [
    AuthGuard,
    AdminAuthGuard,
    CountryAdminAuthGuard,
    AdminOrCountryAdminAuthGuard,
    SubscriptionAuthGuard,
    MatIconService,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CredentialsInterceptor, multi: true },
    { provide: ErrorHandler, useClass: AppErrorHandler },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    // TODO: replace moment-adapter with one of the alternatives e.g. Luxon (moment is obsolete)
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
    { provide: MatPaginatorIntl, useClass: WimtMatPaginatorIntl },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
